import React from 'react'
import { Button, Icon, Spinner } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { observable, action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Api } from 'services/Api';
import axios from 'axios';
import debounce from 'services/debounce';

// Same list as the client search (ClientAutocomplete): a status line, then
// rows with the address and, when it has one, the location's name.
// Styles: .client-suggest-* in styles/search-filters-bar.scss.
const LocationRenderer = (location, { handleClick, modifiers }) => {
  const { full_address, name } = location.attributes;
  const address = full_address || name || 'Location';
  return (
    <li key={location.id}>
      <div
        role="option"
        aria-selected={modifiers.active}
        className={`client-suggest-option${modifiers.active ? ' is-active' : ''}`}
        onClick={handleClick}
      >
        <span className="client-suggest-avatar" aria-hidden="true"><Icon icon="map-marker" iconSize={14} /></span>
        <span className="client-suggest-text">
          <span className="client-suggest-name">{address}</span>
          {name && name !== full_address && <span className="client-suggest-meta">{name}</span>}
        </span>
      </div>
    </li>
  );
};

const plural = (count) => (count === 1 ? 'location' : 'locations');

const ListRenderer = ({ items, itemsParentRef, query, renderItem, loading, failed }) => {
  const renderedItems = items.map(renderItem).filter(item => item != null);
  let status;
  if (failed && !loading) {
    status = 'Couldn’t load locations. Try again.';
  } else if (loading) {
    status = <span className="client-suggest-loading"><Spinner size={14} /> Loading locations…</span>;
  } else if (renderedItems.length === 0) {
    status = query ? `No locations match “${query}”` : 'No locations yet';
  } else {
    status = query
      ? `${renderedItems.length} ${plural(renderedItems.length)} ${renderedItems.length === 1 ? 'matches' : 'match'} “${query}”`
      : `${renderedItems.length} ${plural(renderedItems.length)}`;
  }
  return (
    <div className="client-suggest-menu">
      <div className="client-suggest-status">{status}</div>
      {!loading && !failed && renderedItems.length > 0 && (
        <ul className="client-suggest-list" ref={itemsParentRef} role="listbox">
          {renderedItems}
        </ul>
      )}
    </div>
  );
}

@observer
class LocationAutocomplete extends React.Component {

  @observable loading = true;
  @observable failed = false;
  @observable results = [];
  @observable selectedLocation = null;
  @observable scope = null;

  componentDidUpdate(){
    if(this.props.value){
      this.selectedLocation = this.props.value;
    }
  }

  componentDidMount() {
    let {
      selectedLocationId
    } = this.props;
    if (selectedLocationId) {
      let promise = Api.get(`/locations/${selectedLocationId}`)
      promise.then(response => {
        this.selectedLocation = response.data.data;
      })
    }
    if(this.props.value){
      this.selectedLocation = this.props.value;
    }
  }

  @action
  onQueryChange(query) {
    let { scope } = this.props;
    this.failed = false;
    this.loading = true;
    let search = { term: query, per_page: 50, page: 1, order: { name: 'asc' } };
    if(scope){
      search['where'] = scope;
    }
    let promise = Api.post('/locations/search', { search: search })
    promise
      .then(this.onLocationsLoad.bind(this))
      .catch(this.onLocationsLoadFail.bind(this))
    return promise;
  }

  @action
  onLocationsLoadFail(error) {
    if (axios.isCancel(error)) {
      this.loading = true;
    } else {
      this.loading = false;
      this.failed = true;
    }
  }

  @action
  onLocationsLoad(response) {
    this.loading = false;
    this.failed = false;
    this.results = response.data.data;
  }

  @action
  onLocationSelect(location) {
    this.selectedLocation = location;
    this.props.onLocationSelect(location);
  }

  @action
  clearSelectedLocation() {
    this.selectedLocation = null;
    this.props.onLocationSelect(null);
    this.props.onLocationClear();
  }

  render() {
    let {
      failed,
      results,
      onQueryChange,
      loading,
      onLocationSelect,
      selectedLocation,
      clearSelectedLocation,
      props: {
        inputProps,
        className,
        style
      }
    } = this;
    const wrapperClassName = ['client-name-field', className].filter(Boolean).join(' ');
    return(
      <div className={wrapperClassName} style={style}>
        {
          selectedLocation ? <Button
              ellipsize={true}
              title={
                this.selectedLocation.attributes.full_address
                || this.selectedLocation.attributes.name
              }
              text={
                this.selectedLocation.attributes.full_address
                || this.selectedLocation.attributes.name
              }
              rightIcon='cross'
              onClick={clearSelectedLocation.bind(this)}
            /> : <Suggest
              inputProps={{
                icon: 'cross',
                placeholder: 'Search locations',
                onClick: onQueryChange.bind(this, ""),
                ...inputProps
              }}
              closeOnSelect={true}
              resetOnQuery={true}
              resetOnSelect={true}
              popoverProps={{ minimal: true }}
              onQueryChange={debounce(onQueryChange.bind(this)).bind(this)}
              items={toJS(results)}
              itemRenderer={LocationRenderer}
              itemListRenderer={(props) => <ListRenderer {...props} loading={loading} failed={failed} />}
              inputValueRenderer={(location) => location.attributes.full_address }
              onItemSelect={onLocationSelect.bind(this)}
            />
        }
      </div>
    )
  }
}

LocationAutocomplete.propTypes = {
  inputProps: PropTypes.object,
  onLocationSelect: PropTypes.func.isRequired
}

export default LocationAutocomplete;